import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { createFilterOptions } from '@mui/material/Autocomplete';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import type { FlattenedOption } from './types';
import { flattenVariables } from './utils';
import type { VariableNode } from 'services/entities/VariablesEntity';
import VariablesAccordion from './VariablesAccordion';
import { useCrmPricingEmbedFlag } from 'hooks/useCrmPricingEmbedFlag';

interface AutocompleteVariablesProps {
  variables: VariableNode;
  autoCompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  setIsFreeSoloActive: (value: boolean) => void;
  isEditor?: boolean;
  externalInputValue?: string;
  slotProps?: React.ComponentProps<typeof Autocomplete>['slotProps'] & {
    listbox?: Partial<React.HTMLAttributes<HTMLUListElement>>;
  };
}

const filterOptions = createFilterOptions({
  matchFrom: 'any',
  stringify: (option: FlattenedOption) => option.name || option.friendlyName,
});

export default function AutocompleteVariables({
  variables,
  setAutoCompleteValue,
  autoCompleteValue,
  slotProps,
  setIsFreeSoloActive,
  isEditor,
  externalInputValue,
}: AutocompleteVariablesProps) {
  const { t } = useTranslation();

  const crmPricingEmbedEnabled = useCrmPricingEmbedFlag();

  const options = React.useMemo(() => {
    const flattened = flattenVariables(variables.variables ?? []);
    // RFC 102 clause 7: order preserved once CRM pricing embed ships (SP-3859 rides the same flag
    // as its parent epic); falls back to the old alphabetical-by-path behavior until then.
    return crmPricingEmbedEnabled ? flattened : [...flattened].sort((a, b) => a.path.localeCompare(b.path));
  }, [variables, crmPricingEmbedEnabled]);

  const [accordionState, setAccordionState] = useState({} as Record<string, boolean>);
  const updateAccordionState = useCallback((path: string, isExpanded: boolean) => {
    setAccordionState((prevState) => ({
      ...prevState,
      [path]: isExpanded,
    }));
  }, []);

  useEffect(() => {
    const initialState: Record<string, boolean> = {};
    sortedOptions.forEach((option: any) => {
      const groupPath = option.path.split('/').slice(0, -1).join('/');
      if (!(groupPath in initialState)) {
        initialState[groupPath] = false;
      }
    });
    setAccordionState(initialState);
  }, [options]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        variant="standard"
        sx={{
          margin: isEditor ? '0 0 5px 0' : '0',
          '& .MuiAutocomplete-inputRoot': {
            padding: isEditor ? '0 16px' : '0',
            '&::before': {
              width: isEditor ? 'calc(100% - 32px)' : '100%',
              margin: '0 auto',
            },
            '&.Mui-focused::after': {
              transform: isEditor ? 'scaleX(0)' : 'scaleX(1) translateX(0px)',
            },
          },
        }}
        InputProps={{
          ...params.InputProps,
          startAdornment: autoCompleteValue ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InputAdornment position="start">
                <SearchIcon name="IcoSearch" />
              </InputAdornment>
              <Typography sx={{ color: 'text.secondary', fontSize: '12px', marginLeft: '4px' }}>{t('editor.variables.search')}</Typography>
            </Box>
          ),
          endAdornment: <>{params.InputProps.endAdornment}</>,
        }}
      />
    ),
    [autoCompleteValue]
  );

  const getOptionLabel = (option) => {
    if (typeof option === 'string') return option;
    const label = option.friendlyName;
    return `${label} · [${label.replace(/.*\./, '_')}]`;
  };

  const onInputChangeValue = useCallback(
    (value: string) => {
      const normalizedValue = value.trim().toLowerCase();
      const isSearchedValueExists = options.some(
        (op) =>
          op.path.toLocaleLowerCase().indexOf(normalizedValue) !== -1 || op.friendlyName.toLocaleLowerCase().indexOf(normalizedValue) !== -1
      );
      setAutoCompleteValue(value);
      setIsFreeSoloActive(isSearchedValueExists);
    },
    [setAutoCompleteValue]
  );

  const sortedOptions = React.useMemo(() => {
    if (!crmPricingEmbedEnabled) {
      return [...options].sort((a, b) => {
        const groupA = a.path.split('/').slice(0, -1).join('/');
        const groupB = b.path.split('/').slice(0, -1).join('/');
        return groupA.localeCompare(groupB);
      });
    }

    const groups = new Map<string, FlattenedOption[]>();
    options.forEach((option) => {
      const groupPath = option.path.split('/').slice(0, -1).join('/') || 'root';
      groups.set(groupPath, [...(groups.get(groupPath) ?? []), option]);
    });
    return Array.from(groups.values()).flat();
  }, [options, crmPricingEmbedEnabled]);

  const renderGroup = useCallback(
    (params) => {
      const isExpanded = accordionState[params.key] ?? true;

      return (
        <VariablesAccordion
          key={params.key}
          accordionIdentifier={params.key}
          group={params.group}
          accordionState={isExpanded}
          options={sortedOptions}
          updateAccordionState={updateAccordionState}
          isEditor={isEditor}
        >
          {params.children}
        </VariablesAccordion>
      );
    },
    [accordionState, updateAccordionState, isEditor, sortedOptions]
  );

  return (
    <Autocomplete
      options={sortedOptions}
      disableCloseOnSelect
      {...(externalInputValue !== undefined ? { inputValue: externalInputValue } : {})}
      onInputChange={(_, value) => onInputChangeValue(value)}
      freeSolo
      size="small"
      open={!!autoCompleteValue}
      clearIcon={<ClearIcon fontSize="small" />}
      groupBy={(option) => {
        const groupPath = option.path.split('/').slice(0, -1).join('/');
        return groupPath || 'root';
      }}
      getOptionLabel={getOptionLabel}
      filterOptions={filterOptions}
      slotProps={slotProps}
      renderInput={renderInput}
      renderGroup={renderGroup}
      sx={{ pb: 1.5 }}
    />
  );
}
